<!--App.vue-->
<script>
import { mapGetters } from 'vuex';
import { BApp } from 'bootstrap-vue-next';
import { defineAsyncComponent } from 'vue';

import ToastProvider from './components/ToastProvider.vue';
import TheNavBar from './components/TheNavBar.vue';
import OutramSpinner from './components/OutramSpinner.vue';
import NotificationComponent from './components/NotificationComponent.vue';
import TheSystemMessage from './components/TheSystemMessage.vue';

const ServiceUnavailablePage = defineAsyncComponent(() => 
  import('./views/ServiceUnavailablePage.vue')
);

export default {
  name: 'App',
  components: {
    BApp,
    ToastProvider,
    TheNavBar,
    OutramSpinner,
    NotificationComponent,
    TheSystemMessage,
    ServiceUnavailablePage,
  },
  computed: {
    ...mapGetters({
      newFrontend: 'ui/newFrontendAvailable',
      status: 'ui/status',
      offline: 'ui/offline',
      loading: 'ui/loading',
      loadingMessage: 'ui/loadingMessage',
    }),
    showStatusMessageClose() {
      if (this.offline) return false;
      if (!this.$route.matched.some(route => route.meta.navbar)) return false;
      return true;
    },
  },
  mounted() {
  // Define global responses to Axios Errors
  this.$http.interceptors.response.use(
    resp => resp,
    (error) => {
      if (typeof error.response === 'undefined') {
        // Generic network error
        this.$showToast({
          title: 'Network Error',
          body: 'Unable to Connect to PMGateway',
          variant: 'danger',
          solid: true,
        })
      } else if (error.response.status === 401) {
        if (error.response.config.url === '/user/cookie') {
          throw error
        }
        this.$store.dispatch('user/logout')
          .then(() => this.$router.replace({
            name: 'LoginPage',
            query: {
              session_expired: true,
              from: this.$route.path,
            },
          }))
      } else if (error.response.status === 503) {
        this.$store.dispatch('ui/getStatus')
      } else if (![403, 404, 409, 422].includes(error.response.status)) {
        this.$showToast({
          title: 'An Error Occurred',
          body: `The server responded with a ${error.response.status} error code`,
          variant: 'danger',
          solid: true,
        })
      }
      throw error
    },
  )
  this.$store.dispatch('ui/getStatus')
  },
};
</script>

<template>
  <BApp>
    <toast-provider />
    <!-- Navbar at the Top -->
    <the-nav-bar />
    <the-system-message :show-close="showStatusMessageClose" />

    <!-- Conditional Loading Overlay -->
    <BOverlay
      :show="loading"
      no-wrap
      fixed
    >
      <template #overlay>
        <outram-spinner />
        <h3 class="d-block text-center">
          {{ loadingMessage }}
        </h3>
      </template>
    </BOverlay>

    <!-- Main Content  -->
    <router-view
      v-if="!offline"
      :key="$route.fullPath"
    />
    <service-unavailable-page v-else />

    <!-- Shows a Progress Bar when this.$Progress is used -->
    <!--<vue-progress-bar />-->

    <!-- Alert for when a new Frontend is available -->
    <BAlert
      :show="newFrontend"
      class="position-fixed fixed-bottom m-0 rounded-0"
      style="z-index: 2000;"
      variant="warning"
    >
      <BContainer>
        <BRow align-h="center">
          <BCol cols="auto">
            <p class="text-center lead">
              A new version of this page is available. Please refresh.
            </p>
          </BCol>
        </BRow>
        <BRow align-h="center">
          <BCol cols="auto">
            <BButton
              variant="primary"
              class="text-center"
              @click="$window.location.reload()"
            >
              Refresh
            </BButton>
          </BCol>
        </BRow>
      </BContainer>
    </BAlert>

    <!-- Notifications Modal -->
    <BModal
      id="notification-modal"
      title="Notifications"
      size="sm"
      no-footer
    >
      <notification-component />
    </BModal>
  </BApp>
</template>

<style lang="scss" scoped>

html, body, #app {
  height: 100%;
}
</style>
